import { getBrowser, areCookiesEnabled, setCookieValue, getCookieValue } from "./Utils/Browser";
import { I18nextPseudo } from "./I18nextPseudo";

export interface ModuleFetchDone {
    (resource: object): void;
}

export interface LanguageSources {
    importLanguage: (language: string) => PromiseLike<{}>;
}

export interface LanguageModule {
    name: string;
    fetchResource(language: string, done: ModuleFetchDone): void;
}

export enum Language {
    Dutch = 'nl',
    English = 'en',
    German = 'de',
    Pseudo = 'pseudo'
}

const defaultLanguage = Language.Dutch;

export class I18nLogic {
    private _languageModules: LanguageModule[] = [];
    private _language: KnockoutObservable<string> = ko.observable("");
    private _i18next: i18next.i18n = i18next.createInstance();
    private initialization: Promise<void> | null = null;
    private _pseudo: I18nextPseudo = new I18nextPseudo(false);
    private _browserIsIE: boolean = false;

    constructor() {
        this._browserIsIE = getBrowser().isIE();
    }

    private static doesLanguageExist(language: string): boolean {
        for (const languageItem in Language) {
            if (language === (Language as any)[languageItem]) {
                return true;
            }
        }
        return false;
    }

    public createLanguageModule(name: string, languageSources: LanguageSources): LanguageModule {
        return {
            name: name,
            fetchResource(language: string, done: ModuleFetchDone) {
                if (I18nLogic.doesLanguageExist(language)) {
                    languageSources.importLanguage(language)
                        .then(result => done(result));
                }
                else {
                    console.error("Voor de taal '" + language + "' bestaat geen import in " + name);
                }
            }
        }
    }

    public setLanguage(language: Language): Promise<void> {
        if (this._browserIsIE) {
            return Promise.resolve();
        }
        return this.initialize().then(() => {
            this._i18next.changeLanguage(language, () => {
                return this.downloadLanguageModulesResources(language).then(() => {
                    this.setCookie(language);
                    this._language(language);
                });
            });
        });
    }

    private downloadLanguageModulesResources(language: Language): Promise<void[]> {
        if (!Array.isArray(this._languageModules) || this._languageModules.length === 0) {
            return Promise.all([Promise.resolve()]);
        }

        const promises = new Array<Promise<void>>();
        this._languageModules.forEach((currentModule) => {
            promises.push(this.downloadLanguageModuleResource(language, currentModule));
        });
        return Promise.all(promises);
    }

    private setCookie(language: Language): void {
        if (areCookiesEnabled()) {
            setCookieValue("language", language, 30, false);
        }
    }

    public t(key: string | string[], options?: i18next.TOptions): string {
        if (this._browserIsIE) {
            return "";
        }
        this.initialize();
        return this._i18next.t(key, options);
    }

    public computed_t(key: string | string[], options?: i18next.TOptions): KnockoutComputed<string> {
        return ko.computed(() => {
            this._language(); // to observe language changes
            return this.t(key, options);
        });
    }

    public addLanguageModule(moduleDefaultResource: object, languageModule: LanguageModule): void {
        if (this._browserIsIE) {
            return;
        }
        this.initialize();
        this._languageModules.push(languageModule);
        this._i18next.addResourceBundle(defaultLanguage, languageModule.name, moduleDefaultResource);
    }

    public togglePseudo(): Promise<void> {
        if (this._browserIsIE) {
            return Promise.resolve();
        }
        return this.initialize().then(() => {
            this._pseudo.enablePseudo = !this._pseudo.enablePseudo;
            this._language.valueHasMutated!();
        });
    }

    public isPseudoEnabled(): boolean {
        return this._pseudo.enablePseudo;
    }

    private downloadLanguageModuleResource(language: string, languageModule: LanguageModule): Promise<void> {
        if (language !== defaultLanguage) {
            return new Promise<void>((resolve) => {
                languageModule.fetchResource(language, (resource: object) => {
                    this._i18next.addResourceBundle(language, languageModule.name, resource);
                    resolve();
                })
            });
        } else {
            return Promise.resolve();
        }
    }

    public initialize(): Promise<void> {
        if (!this.initialization) {
            let language: Language;
            this._language = ko.observable("");
            const enablePseudo = getCookieValue("pseudo") === "true";
            this._pseudo = new I18nextPseudo(enablePseudo);

            const languageCookieValue = getCookieValue("language");
            if (areCookiesEnabled() && I18nLogic.doesLanguageExist(languageCookieValue)) {
                language = languageCookieValue as Language;
            } else {
                language = defaultLanguage;
            }

            this._i18next = i18next.createInstance();
            this.initialization = this._i18next
                .use(this._pseudo)
                .init({ debug: true, lng: language, fallbackLng: defaultLanguage, defaultNS: '', initImmediate: false, postProcess: ['pseudo'] })
                .then(() => {
                    this.setI18nBindingHandler();
                    this.setCookie(language);
                    return this.downloadLanguageModulesResources(language).then(() => this._language(language));
                });
        }
        return this.initialization;
    }

    public resetInitialization() {
        this.initialization = null;
    }

    private setI18nBindingHandler(): void {
        /*
         * Verwijzingen naar vertalingen mogen 
         * 1. zich 'los' in een i18n data-bind bevinden: 
         *       i18n.common.button.opslaan
         * 2. zich in een object met een veld 'key' en een optioneel veld 'options' bevinden: 
         *       { key: i18n.werkbonnen.historie.bezoeken, options: { aantal: 5 } }
         * 3. zich in een object met een verwijzing naar een attribuut bevinden, in combinatie met een losse key: 
         *       { title: i18n.common.button.opslaan }
         * 4. zich in een object met een verwijzing naar een attribuut bevinden, in combinatie met een 'key' en 'options':
         *       { title: { key: i18n.werkbonnen.historie.bezoeken, options: { aantal: 5 } } }
         */
        ko.bindingHandlers.i18n = {
            update: (element, valueAccessor) => {
                this._language(); // op deze manier gaat de update functie af als de language observable wijzigt

                const getTranslationErrorTekst = () => {
                    console.error("Er is geen key gevonden voor de data-bind in het element: \n", element);
                    return "<Tekst kan niet worden bepaald>";
                }

                let value;
                try {
                    value = ko.toJS(valueAccessor());
                } catch (error) { // afvangen typfout in verwijzing in parent niveau, bijv i18n.cXmmon.button.opslaan
                    element.innerText = getTranslationErrorTekst();
                    return;
                }

                if (typeof value === 'undefined') { // afvangen typfout in verwijzing in diepste niveau, bijv i18n.common.button.Xpslaan
                    element.innerText = getTranslationErrorTekst();
                } else if (typeof value === 'string') {
                    element.innerText = this.t(value);
                } else if (typeof value.key === 'string') {
                    element.innerText = this.t(value.key, value.options);
                } else {
                    for (const propertyName in value) {
                        const propertyValue = value[propertyName];
                        let translation;
                        if (typeof propertyValue === 'undefined') { // afvangen typfout wanneer key zich in object bevindt, bijv { key: i18n.common.button.Xpslaan }
                            translation = getTranslationErrorTekst();
                        } else if (typeof propertyValue === 'string') {
                            translation = this.t(propertyValue);
                        } else if (typeof propertyValue.key === 'string') {
                            translation = this.t(propertyValue.key, propertyValue.options);
                        } else {
                            translation = getTranslationErrorTekst(); // afvangen edge cases
                        }
                        element.setAttribute(propertyName, translation);
                    }
                }
            }
        }
    }
}

export let i18n = (window as any).i18n = (window as any).i18n as I18nLogic || new I18nLogic();
